import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { Award, Users, Building2, Globe, ArrowRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

export function ExperienceSection() {
  const { t } = useTranslation()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  })

  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%'])

  const highlights = [
    { icon: Award, value: '36+', label: t('company.statYearsLabel') },
    { icon: Building2, value: '5', label: t('locations.localUnits') },
    { icon: Users, value: '125', label: t('company.statOperatorsLabel') },
    { icon: Globe, value: '11.9K+', label: t('company.statProjectsLabel') },
  ]

  return (
    <section ref={ref} className="section relative overflow-hidden bg-[var(--color-void)]">
      {/* Background Elements */}
      <motion.div
        className="absolute -right-40 -top-40 h-[600px] w-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(247, 94, 71, 0.08) 0%, transparent 70%)',
          y: backgroundY,
        }}
      />

      {/* Noise Texture */}
      <div className="noise-overlay" />

      {/* Diagonal Accent */}
      <div className="absolute left-0 top-0 h-[1px] w-[150%] -translate-x-1/4 rotate-[25deg] bg-gradient-to-r from-transparent via-[var(--color-graphite)] to-transparent" />

      <div className="container relative z-10">
        <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-24">
          {/* Left: Text content */}
          <div>
            {/* Section Label */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="section-label"
            >
              <span className="label">La Nostra Storia</span>
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="headline-xl mt-6"
            >
              {t('homepage.experience.title', { years: 36 })}
            </motion.h2>

            {/* Accent Line */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 h-[2px] w-20 origin-left bg-gradient-to-r from-[var(--accent-primary)] to-transparent"
            />

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="body-lg mt-8"
            >
              {t('homepage.experience.description1', { year: 1988 })}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="body-md mt-4"
            >
              {t('homepage.experience.description2')}
            </motion.p>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10"
            >
              <LocalizedLink
                to="chiSiamo"
                className="btn btn-primary group"
              >
                {t('homepage.experience.aboutLink')}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </LocalizedLink>
            </motion.div>
          </div>

          {/* Right: Stats Grid */}
          <div className="relative">
            {/* Decorative Frame */}
            <div className="absolute -inset-4 border border-[var(--color-graphite)] lg:-inset-6" />
            <div className="absolute -inset-4 border border-[var(--color-graphite)] lg:-inset-6 translate-x-2 translate-y-2" />

            <div className="relative grid grid-cols-2 gap-[1px] bg-[var(--color-graphite)]">
              {highlights.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    duration: 0.6,
                    delay: 0.3 + index * 0.1,
                    ease: [0.16, 1, 0.3, 1]
                  }}
                  whileHover={{ scale: 1.02, zIndex: 10 }}
                  className="group relative bg-[var(--color-ink)] p-6 md:p-8 transition-all duration-500 hover:bg-[var(--color-charcoal)]"
                >
                  {/* Icon */}
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--color-graphite)] text-[var(--accent-primary)] transition-all duration-300 group-hover:bg-[var(--accent-primary)] group-hover:text-white">
                    <item.icon className="h-5 w-5" />
                  </div>

                  {/* Value */}
                  <div className="stat-number text-3xl md:text-4xl transition-colors group-hover:text-[var(--accent-primary)]">
                    {item.value}
                  </div>

                  {/* Label */}
                  <div className="stat-label">
                    {item.label}
                  </div>

                  {/* Hover Accent */}
                  <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[var(--accent-primary)] transition-all duration-500 group-hover:w-full" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
